import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  getSingle,
  removeImage,
  stationAddLogo,
  stationAddImages,
  stationEdit,
} from "actions/chargers/station/actions";
import { getAdminsByRole } from "actions/admin/actions";
import { Redirect } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import getFilesUrl from "../../../../helpers/getFilesUrl";
import Translations from "./translations";
import { PB_STATION_STATUSES, PM_STATION_NETWORK_MODES, PM_STATION_TYPES } from "./constants";
import filterTypes from "../../../../data/filter-types";
import StationLocation from "./StationLocation";
import { INITIAL_STATION_STATE } from "./initialStationState";
import Owners from "./Owners";

const languageTemplate = {
  destinationAddress: "",
  destinationName: "",
};

const NEW_IMAGE_PREFIX = "newId-";

const EditStation = (props) => {
  const { getSingle } = props;
  const id = props.match.params.id;
  const [redirectTo, setRedirectTo] = useState("");
  const [previewLogo, setPreviewLogo] = useState({});
  const [previewImages, setPreviewImages] = useState([]);
  const [formState, setFormState] = useState({
    ...INITIAL_STATION_STATE,
    id,
  });

  const handleChange = (ev) => {
    ev.persist();
    setFormState((prevState) => ({
      ...prevState,
      [ev.target.name]: ev.target.value
    }));
  };

  const handleChangeOwners = (value) => {
    setFormState((prevState) => ({
      ...prevState,
      owners: value,
    }));
  };

  const handleSubmit = async () => {
    const {
      images,
      logo,
      ...rest
    } = formState;
    const payloadAll = {
      ...rest,
      id
    };
    const payloadKeys = Object.keys(INITIAL_STATION_STATE);
    const payload = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in payloadAll) {
      if (payloadKeys.includes(key)) {
        payload[key] = payloadAll[key];
      }
    }
    if (payload.owners) {
      payload.owners = payload.owners.filter(owner => owner.percent);
    }

    if (Array.isArray(payload.owners) && payload.owners.reduce((total, current) => total + current.percent, 0) > 100) {
      toast("Total percent of all owners is more than 100", {
        type: toast.TYPE.WARNING,
      });
      return;
    }

    props.stationEdit(id, payload, async (response) => {
      if (!response || !response.data || !response.data.content.id) {
        return;
      }

      try {
        const newItemId = response.data.content.id;
        const promises = [];
        if (previewLogo.file) {
          const p = props.stationAddLogo(newItemId, previewLogo.file);
          promises.push(p);
        }

        if (previewImages.length) {
          // eslint-disable-next-line no-restricted-syntax
          for (const img of previewImages) {
            if (img.file) {
              const p = props.stationAddImages(newItemId, img.file);
              promises.push(p);
            }
          }
        }

        await Promise.all(promises);
        toast("Changes saved successfully", {
          type: toast.TYPE.SUCCESS,
        });
      } catch (error) {
        toast(error, {
          type: toast.TYPE.ERROR,
        });
      } finally {
        setRedirectTo("/chargers/stations/list");
      }
    });
  };

  const handleChangeLanguage = (lang, name, value) => {
    const translations = [...formState.translations];

    const languageItemIndex = translations.findIndex(i => i.language === lang);
    const languageItem = (languageItemIndex === -1) ? { ...languageTemplate } : { ...translations[languageItemIndex] };

    languageItem.language = lang;
    languageItem[name] = value;

    setFormState((prevState) => ({
      ...prevState,
      translations: [
        ...prevState.translations.filter(i => i.language !== lang),
        languageItem
      ]
    }));
  };

  const handleChangeLocation = (coordinatesArray) => {
    setFormState((prevState) => ({
      ...prevState,
      location: {
        longitude: coordinatesArray.length ? coordinatesArray.find(item => item.key === "longitude").value : prevState.location.longitude,
        latitude: coordinatesArray.length ? coordinatesArray.find(item => item.key === "latitude").value : prevState.location.latitude,
      }
    }));
  };

  const handleChangeLogo = (ev) => {
    const file = ev.target.files[0];
    if (!file) {
      return;
    }
    setPreviewLogo({
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleChangeImages = (ev) => {
    const file = ev.target.files[0];
    if (!file) {
      return;
    }
    setPreviewImages([...previewImages, {
      file,
      preview: URL.createObjectURL(file),
      id: `${NEW_IMAGE_PREFIX}${Math.random()}`
    }]);
  };

  const handleRemoveImage = (imageId) => {
    const imageToRemove = previewImages[0];
    if (id && imageToRemove.id && !imageId.includes(NEW_IMAGE_PREFIX)) {
      props.removeImage(id, imageToRemove.id)
        .then(() => {
          toast("Image removed successfully", {
            type: toast.TYPE.SUCCESS,
          });
        });
    }
    setPreviewImages(previewImages.filter((image) => image.id !== imageId));
  };

  useEffect(() => {
    if (id) {
      getSingle([{
        fieldName: "id",
        fieldValue: id,
        searchOperation: filterTypes.EQUALS.value
      }], (response) => {
        if (response && response.content && response.content.length) {
          const item = response.content[0];
          setFormState(item);
          setPreviewLogo({
            file: false,
            preview: item && item.logo ? getFilesUrl(item.logo) : "",
          });
          setPreviewImages((item.images ? item.images : []).map(image => ({
            id: image.id,
            file: false,
            preview: image ? getFilesUrl(image) : "",
          })));
        }
      });
    }
  }, [id, getSingle]);

  if (redirectTo) {
    return <Redirect to={redirectTo}/>;
  }

  return (
    <section className="w-100 p-2">
      <div className="card">
        <div className="card-header">
          <h4>{id ? "Edit station" : "Create new station"}</h4>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label>QR</label>
                <input
                  className="form-control form-control-sm"
                  name="qr"
                  value={formState.qr}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Type</label>
                <select
                  className="form-control form-control-sm"
                  name="type"
                  value={formState.type}
                  readOnly={true}
                >
                  {Object.keys(PM_STATION_TYPES)
                    .map((key) => (
                      <option
                        key={key}
                        value={PM_STATION_TYPES[key]}
                      >
                        {PM_STATION_TYPES[key]}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Network Mode</label>
                <select
                  className="form-control form-control-sm"
                  name="networkMode"
                  readOnly={true}
                  value={formState.networkMode}
                >
                  {Object.keys(PM_STATION_NETWORK_MODES)
                    .map((key) => (
                      <option
                        key={key}
                        value={PM_STATION_NETWORK_MODES[key]}
                      >
                        {PM_STATION_NETWORK_MODES[key]}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control form-control-sm"
                  name="status"
                  value={formState.status}
                  readOnly={true}
                >
                  {Object.keys(PB_STATION_STATUSES)
                    .map((key) => (
                      <option
                        key={key}
                        value={PB_STATION_STATUSES[key]}
                      >
                        {PB_STATION_STATUSES[key]}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Country</label>
                <input
                  className="form-control form-control-sm"
                  name="country"
                  value={formState.country}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Hardware Version</label>
                <input
                  className="form-control form-control-sm"
                  name="hardwareVersion"
                  value={formState.hardwareVersion}
                  readOnly={true}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Software Version</label>
                <input
                  className="form-control form-control-sm"
                  name="softwareVersion"
                  value={formState.softwareVersion}
                  readOnly={true}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Working Hours</label>
                <input
                  className="form-control form-control-sm"
                  name="workingHours"
                  value={formState.workingHours}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Discount</label>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  name="discount"
                  value={formState.discount}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Slots count</label>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  name="slotsCount"
                  value={formState.slotsCount}
                  readOnly={true}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>PowerBanks Count</label>
                <input
                  className="form-control form-control-sm"
                  type="number"
                  name="powerBanksCount"
                  value={formState.powerBanksCount}
                  readOnly={true}
                />
              </div>
            </div>

            <div className="col-6">
              <div className="form-group">
                <label>Website URL</label>
                <input
                  className="form-control form-control-sm"
                  name="websiteUrl"
                  value={formState.websiteUrl}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Linkedin URL</label>
                <input
                  className="form-control form-control-sm"
                  name="linkedinUrl"
                  value={formState.linkedinUrl}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Facebook URL</label>
                <input
                  className="form-control form-control-sm"
                  name="facebookUrl"
                  value={formState.facebookUrl}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label>Instagram URL</label>
                <input
                  className="form-control form-control-sm"
                  name="instagramUrl"
                  value={formState.instagramUrl}
                  onChange={handleChange}
                />
              </div>
            </div>

            <Owners activeOwners={formState.owners} onChange={handleChangeOwners}/>
          </div>
        </div>
        <hr/>
        <div className="card-body">
          <Translations
            translations={formState.translations}
            onChange={handleChangeLanguage}
          />
        </div>
        <hr/>

        <div className="card-body text-right">
          <StationLocation
            onChange={handleChangeLocation}
            {...formState.location}
          />
        </div>
        <hr/>

        <div className="card-body text-right">
          <div className="form-group">
            <label>Logo</label>
            <input
              className="form-control"
              accept="image/*"
              type="file"
              name="logo"
              placeholder="Upload Logo"
              onChange={handleChangeLogo}
            />
            {previewLogo && previewLogo.preview && (
              <div className="mb-3 text-center">
                <a target="_blank" rel="noopener noreferrer" href={previewLogo.preview}>
                  <img
                    src={previewLogo.preview}
                    alt="Preview"
                    className="img-thumbnail"
                    style={{
                      maxWidth: "100%",
                      width: "auto",
                      marginTop: 20,
                      height: 200,
                    }}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
        <hr/>
        <div className="card-body text-right">
          <div className="form-group">
            <label>Images</label>
            <input
              className="form-control"
              accept="image/*"
              type="file"
              name="image"
              placeholder="Upload Logo"
              onChange={handleChangeImages}
            />
            <div className="row">
              {previewImages && previewImages.length ? previewImages.map((item, index) => (
                <div key={index} className="col-3 mb-3 text-center position-relative">
                  <div className="position-absolute" style={{
                    bottom: 0,
                    right: 0
                  }}>
                    <button className="btn btn-sm btn-danger" onClick={(ev) => handleRemoveImage(item.id)}>
                      <FontAwesomeIcon icon={faTimes}/> Remove
                    </button>
                  </div>
                  <a target="_blank" rel="noopener noreferrer" href={item.preview}>
                    <img
                      src={item.preview}
                      alt="Preview"
                      className="img-thumbnail"
                      style={{
                        maxWidth: "100%",
                        width: "auto",
                        marginTop: 20,
                        height: 100,
                      }}
                    />
                  </a>
                </div>
              )) : null}
            </div>
          </div>
        </div>

        <hr/>
        <div className="card-body text-right pt-0">
          <button
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={false}
          >
            Save
          </button>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = state => ({
  packageImage: state.chargerPackage.packageImage,
});

const mapStateToDispatch = dispatch => ({
  getAdminsByRole: bindActionCreators(getAdminsByRole, dispatch),
  stationEdit: bindActionCreators(stationEdit, dispatch),
  getSingle: bindActionCreators(getSingle, dispatch),
  stationAddLogo: bindActionCreators(stationAddLogo, dispatch),
  stationAddImages: bindActionCreators(stationAddImages, dispatch),
  removeImage: bindActionCreators(removeImage, dispatch),
});

export default connect(
  mapStateToProps,
  mapStateToDispatch
)(EditStation);



// WEBPACK FOOTER //
// ./src/views/pages/chargers/stations/edit.js